"use client";
/* eslint-disable @typescript-eslint/no-explicit-any */
import { iconList } from "@/assets/icons";
import {
  Controller,
  FormProvider,
  SubmitHandler,
  useForm,
} from "react-hook-form";
import { ArrowRight } from "lucide-react";
import { useState } from "react";
import { DateRange } from "react-day-picker";
import { format } from "date-fns";
import {
  datePickerFormatJP,
  DEFAULT_ADULTS,
  DEFAULT_CHECKIN_DATE,
  DEFAULT_CHECKOUT_DATE,
} from "@/constants";
import { useModal } from "@/hooks/useModal/useModal";
import { GroupOption } from "./SearchAvailableRoom";
import { Label } from "@radix-ui/react-label";
import { RoomSelect } from "./RoomSelect";
import { valibotResolver } from "@hookform/resolvers/valibot";
import { IHotelOrCity } from "@/types/availableRoom";
import { ERoomOrderBy, ERoomType } from "@/types/roomsList";
import { TRIPLA_BOOKING_URL } from "@/constants/enviroment";
import { useValidatedDates } from "@/hooks/useValidatedDates";
import {
  cityAndHotelFuzzyFilter,
  formatDateToYYYYMMDD,
  redirectBookingCard,
  removeTrailingSlashPathName,
  SelectOption,
  formatDateForStorage,
  checkAndValidateStoredDates,
} from "@/utils";
import { LOCALE_BASE_MAP } from "@/i18n/routing";
import * as React from "react";

import * as v from "valibot";

import { useIsMobile } from "@/hooks/useIsMobile/useIsMobile";
import useJapanAreasStore from "@/store/hooks/useGlobal";
import useSearchFormStore from "@/store/searchFormStore/searchFormStore";
import { removeSlugHotelDetailPrefix } from "@/utils/queryUtils/queryUtils";
import { usePathname } from "next/navigation";
import { createBookingParams } from "./ultils";
import { CustomSelect } from "@/components/atoms/dropdown";
import { Button } from "@/components/atoms/button";
import { Modal } from "@/components/atoms/modal";
import { Icon } from "@/components/atoms/icon";
import { DateRangePicker } from "@/components/atoms/date-range-picker";
import { Input } from "@/components/atoms/input";
// import { CLIENT_ENV } from "@/configs/config";

export interface AgesConfigurations {
  agesLabel?: string;
  agePlaceholder?: string;
  singleChildLabel?: string;
  ageValuePlaceholder?: string;
  invalidAgeError?: string;
}

export interface Booking {
  bookingButtonText?: string;
}

export interface RoomsConfigurations {
  adultLabel?: string;
  childrenLabel?: string;
  addRoomButtonText?: string;
  maximumAdults?: string;
  maximumChildAge?: string;
  maximumChildren?: string;
  maximumRooms?: string;
  singleRoomLabel?: string;
  bedLabel?: string;
  withoutBedLabel?: string;
  withBedLabel?: string;
  default_adult_count?: string;
}

export interface Configurations {
  checkinLabel?: string;
  checkoutLabel?: string;
  promoCodeLabel?: string;
  promoCodePlaceholder?: string;
}

export interface CustomSearchAvailableProps {
  className?: string;
  isSearchByHotelAndCity?: boolean;
  listOption: SelectOption[] | GroupOption[];
  trigger?: React.ReactNode;
  configurations?: Configurations;
  roomsConfigurations?: RoomsConfigurations;
  booking?: Booking;
  agesConfigurations?: AgesConfigurations;
  locale: string;
  hotelId?: string;
  promoCode?: string;
  phone?: string;
  timings?: string;
  showDate?: boolean;
  isHiddenMobile?: boolean;
}

function extractKidsAndChildren(rooms: SearchAvailableRoom["rooms"]) {
  const kidsAges: { bed: boolean; age: string; count: number }[] = [];
  let totalChildren = 0;

  if (!rooms) {
    return { totalChildren, kidsAges };
  }

  // Count total children
  for (const room of rooms) {
    if (!room) continue;
    totalChildren += room.children?.length || 0;
  }

  // Build kidsAges from ONLY room[0]
  if (rooms[0]?.children) {
    for (const child of rooms[0].children) {
      if (!child) continue;

      const age = child.age?.value || 0;
      const isBed = child.isBed || false;

      kidsAges.push({
        bed: isBed,
        age: age.toString(),
        count: 1,
      });
    }
  }

  return { totalChildren, kidsAges };
}

export const mapToRequestObject = (data: SearchAvailableRoom) => {
  const { checkIn, checkout, rooms, promoCode, hotelOrCity } = data;

  // const code = CLIENT_ENV === 'production' ? hotelOrCity?.bookingId ?? '' : "01946e82-140c-7fe5-ab6e-60de45c787b1";
  const code = hotelOrCity?.bookingId ?? "";


  // Extract values using helper — SAME LOGIC as original
  const { totalChildren, kidsAges } = extractKidsAndChildren(rooms);

  return {
    code,
    checkin: checkIn ? formatDateToYYYYMMDD(checkIn) : undefined,
    checkout: checkout ? formatDateToYYYYMMDD(checkout) : undefined,
    adults: rooms?.[0]?.adults.toString() || "1",
    children: totalChildren,
    kids_ages: kidsAges,
    room_count: rooms?.length,
    coupon_code: promoCode,
  };
};

export const schemaSearchAvailableRoom = v.object({
  hotelOrCity: v.nullish(
    v.object({
      label: v.string(),
      value: v.string(),
      type: v.optional(v.string()),
      city: v.optional(v.string()),
      bookingId: v.optional(v.string()),
      hotelCode: v.optional(v.string()),
      lat: v.optional(v.string()),
      lng: v.optional(v.string()),
      hotelItemName: v.optional(v.string()),
      slug: v.optional(v.string()),
      zoom: v.optional(v.number()),
      useTripla: v.optional(v.boolean()),
      externalUrl: v.optional(v.string()),
    }),
  ),
  checkIn: v.nullable(v.date()),
  checkout: v.nullable(v.date()),
  rooms: v.optional(
    v.array(
      v.optional(
        v.object({
          adults: v.number(),
          children: v.array(
            v.optional(
              v.object({
                age: v.optional(
                  v.object({
                    label: v.string(),
                    value: v.number(),
                  }),
                ),
                isBed: v.optional(v.boolean()),
              }),
            ),
          ),
        }),
      ),
    ),
  ),
  promoCode: v.optional(v.string()),
  brands: v.optional(
    v.array(
      v.object({
        label: v.string(),
        value: v.string(),
      }),
    ),
  ),
  preferences: v.optional(
    v.array(
      v.object({
        label: v.string(),
        value: v.string(),
      }),
    ),
  ),
});

export type SearchAvailableRoom = v.InferOutput<
  typeof schemaSearchAvailableRoom
>;

const SearchAvailableRoomMobile: React.FC<CustomSearchAvailableProps> = ({
  listOption,
  trigger,
  configurations,
  roomsConfigurations,
  booking,
  agesConfigurations,
  hotelId,
  promoCode,
  locale,
  ...props
}) => {
  const [showValidationError, setShowValidationError] = useState(false);
  const { AngleRightSmall } = iconList;
  const { setFormData, formData } = useSearchFormStore();
  const { japanAreas, loading } = useJapanAreasStore();
  const isMobile = useIsMobile();
  const currentPathName = usePathname();
  const isDestinationPage =
    currentPathName.includes("/japan") || currentPathName.endsWith("-japan");
  // Robust: always update range from localStorage/store when modal opens
  const [range, setRange] = useState<DateRange>({
    from: new Date(DEFAULT_CHECKIN_DATE),
    to: new Date(DEFAULT_CHECKOUT_DATE),
  });

  const [inputValue, setInputValue] = useState<string>("");
  const [previousRange, setPreviousRange] = useState<DateRange>({
    from: new Date(DEFAULT_CHECKIN_DATE),
    to: new Date(DEFAULT_CHECKOUT_DATE),
  });

  const [currentSelectedHotel, setCurrentSelectedHotel] =
    useState<IHotelOrCity | null>(() => {
      if (hotelId) {
        for (const item of listOption) {
          if ("options" in item) {
            const found = item.options.find((opt) => {
              return opt.value === hotelId || opt.bookingId === hotelId;
            });
            if (found) return found as IHotelOrCity;
          } else if (item.value === hotelId || item.bookingId === hotelId) {
            return item as IHotelOrCity;
          }
        }
      }
      return formData?.hotelOrCity || null;
    });

  const { checkIn: validatedCheckIn, checkOut: validatedCheckOut } =
    useValidatedDates(formData?.checkIn, formData?.checkout);

  // Helper: Find hotel from path
  const findHotelFromPath = (
    currentPathName: string,
    listOption: SelectOption[] | GroupOption[],
  ): IHotelOrCity | null => {
    const pathArr = removeTrailingSlashPathName(currentPathName).split("/");
    const hotelSlugFromPath = removeSlugHotelDetailPrefix(
      pathArr[(pathArr?.length ?? 0) - 1],
    );

    if (!hotelSlugFromPath) return null;

    for (const item of listOption) {
      if ("options" in item) {
        const found = item.options.find(
          (opt) => opt.slug === hotelSlugFromPath,
        );
        if (found) {
          return found as IHotelOrCity;
        }
      }
    }
    return null;
  };

  // Helper: Determine initial hotel or city
  const determineInitialHotelOrCity = (
    hotelId: string | undefined,
    currentSelectedHotel: IHotelOrCity | null,
    formData: SearchAvailableRoom | null,
    currentPathName: string,
    listOption: SelectOption[] | GroupOption[],
  ): IHotelOrCity | null => {
    if (hotelId && currentSelectedHotel) {
      return currentSelectedHotel;
    }

    if (formData?.hotelOrCity) {
      return formData.hotelOrCity;
    }

    return findHotelFromPath(currentPathName, listOption);
  };

  // Helper: Determine rooms value
  const determineRooms = (formData: SearchAvailableRoom | null) => {
    if (formData?.rooms && formData.rooms.length > 0) {
      return formData.rooms;
    }
    return [{ adults: Number(DEFAULT_ADULTS), children: [] }];
  };

  // Helper: Determine promo code
  const determinePromoCode = (
    formData: SearchAvailableRoom | null,
    promoCode: string | undefined,
  ): string => {
    return formData?.promoCode || promoCode || "";
  };

  const initialFormDataState = React.useMemo(() => {
    const initialHotelOrCity = determineInitialHotelOrCity(
      hotelId,
      currentSelectedHotel,
      formData,
      currentPathName,
      listOption,
    );

    const { checkIn, checkout } = {
      checkIn: validatedCheckIn,
      checkout: validatedCheckOut,
    };
    const rooms = determineRooms(formData);
    const promoCodeValue = determinePromoCode(formData, promoCode);

    return {
      hotelOrCity: initialHotelOrCity,
      checkIn,
      checkout,
      rooms,
      promoCode: promoCodeValue,
      preferences: formData?.preferences || [],
      brands: formData?.brands || [],
    } as SearchAvailableRoom;
  }, [
    determineInitialHotelOrCity,
    hotelId,
    currentSelectedHotel,
    formData,
    currentPathName,
    listOption,
    validatedCheckIn,
    validatedCheckOut,
    promoCode,
  ]);

  const methods = useForm<SearchAvailableRoom>({
    defaultValues: initialFormDataState,
    resolver: valibotResolver(schemaSearchAvailableRoom),
  });

  const { handleSubmit, control, formState, setValue } = methods;
  const { isOpen, toggleModal } = useModal();

  const handleToggleModal = React.useCallback(() => {
    const nextIsOpen = !isOpen;
    if (nextIsOpen) {
      const latestFormData = useSearchFormStore.getState().formData;
      const { checkIn: freshCheckIn, checkOut: freshCheckOut } = checkAndValidateStoredDates(
        latestFormData?.checkIn,
        DEFAULT_CHECKIN_DATE,
        latestFormData?.checkout,
        DEFAULT_CHECKOUT_DATE,
      );
      setRange({ from: freshCheckIn, to: freshCheckOut });
      setValue('checkIn', freshCheckIn);
      setValue('checkout', freshCheckOut);
    }
    toggleModal();
  }, [isOpen, toggleModal, setValue]);

  React.useEffect(() => {
    const pathArr =
      removeTrailingSlashPathName(currentPathName)?.split("/") || [];
    const hotelSlug = pathArr?.[(pathArr?.length ?? 0) - 1];

    if (hotelSlug) {
      let foundHotel: IHotelOrCity | null = null;
      const cleanHotelSlug = removeSlugHotelDetailPrefix(hotelSlug);

      for (const item of listOption) {
        if ("options" in item) {
          const found = item.options.find((opt) => {
            return (
              opt.slug === hotelSlug ||
              opt.slug === cleanHotelSlug ||
              (hotelId && (opt.value === hotelId || opt.bookingId === hotelId))
            );
          });
          if (found) {
            foundHotel = found as IHotelOrCity;
            break;
          }
        }
      }
      if (
        foundHotel &&
        foundHotel.value !== methods.getValues("hotelOrCity")?.value
      ) {
        setValue("hotelOrCity", foundHotel, { shouldValidate: true });
        setCurrentSelectedHotel(foundHotel);
      }
    }
  }, [currentPathName, setValue, methods, hotelId, listOption]);

  const handleRangeSelect = (
    nextRange: DateRange | undefined,
    selectedDay: Date | undefined,
  ) => {
    // Save previous range for cancel functionality
    if (range?.from && range?.to) {
      setPreviousRange({ from: range.from, to: range.to });
    }
    // If a range is already selected, clicking any date always starts a new selection
    if (range?.from && range?.to && selectedDay) {
      setRange({ from: selectedDay, to: undefined });
      setValue('checkIn', selectedDay);
      setValue('checkout', null);
      setFormData({
        ...(formData as SearchAvailableRoom),
        checkIn: formatDateForStorage(range.from, DEFAULT_CHECKIN_DATE),
        checkout: formatDateForStorage(range.to, DEFAULT_CHECKOUT_DATE),
      } as any);
      return;
    }
    // Only persist to form when both from and to are selected
    if (nextRange?.from && nextRange?.to) {
      setValue('checkIn', nextRange.from);
      setValue('checkout', nextRange.to);
      setRange(nextRange);
      setFormData({
        ...(formData as SearchAvailableRoom),
        checkIn: formatDateForStorage(nextRange.from, DEFAULT_CHECKIN_DATE),
        checkout: formatDateForStorage(nextRange.to, DEFAULT_CHECKOUT_DATE),
      } as any);
    } else {
      setRange(nextRange || { from: undefined, to: undefined });
    }
  };


  React.useEffect(() => {
    if (isDestinationPage) {
      if (
        formData?.hotelOrCity &&
        formData.hotelOrCity !== currentSelectedHotel
      ) {
        setCurrentSelectedHotel(formData.hotelOrCity);
      }

      if (formData?.hotelOrCity?.label) {
        setInputValue(formData.hotelOrCity.label);
      }
    }
  }, [currentSelectedHotel, formData, isDestinationPage]);

  // Helper: Route booking to appropriate destination
  const routeBooking = async (values: SearchAvailableRoom): Promise<void> => {
    if (values.hotelOrCity?.type === "city") {
      const slug_hotel =
        japanAreas?.find(
          (item) =>
            item?.area_name?.toLowerCase() ===
            values?.hotelOrCity?.label.toLowerCase(),
        )?.slug_hotel || "";
      const cityListRoute = `${globalThis.location.origin}/${locale}/${slug_hotel}`;
      window.open(cityListRoute, isMobile ? "_self" : "_blank");
      toggleModal();
    } else if (values.hotelOrCity?.useTripla === false) {
      window.open(
        values.hotelOrCity?.externalUrl || values.hotelOrCity?.slug,
        isMobile ? "_self" : "_blank",
      );
    } else {
      const response = await fetch('/api/tripla/access-token');
      const tokenData = await response?.json() ?? {};
      const oneTimeToken = tokenData?.one_time_access_token ?? '';
      const queryParams = createBookingParams(values, {
        type: ERoomType.ROOMS,
        is_day_use: false,
        order: ERoomOrderBy.RECOMMENDED,
        is_including_occupied: false,
        locale: LOCALE_BASE_MAP[locale],
        coupon_code: values?.promoCode || "",
        ...(oneTimeToken && { one_time_access_token: oneTimeToken }),
      });

      if (TRIPLA_BOOKING_URL && queryParams.code) {
        redirectBookingCard(
          {
            ...queryParams,
            checkin: queryParams?.checkin || DEFAULT_CHECKIN_DATE,
            checkout: queryParams?.checkout || DEFAULT_CHECKOUT_DATE,
            adults: queryParams?.adults || "2",
            room_count: queryParams?.room_count || 1,
            children: queryParams?.children,
            kids_ages: queryParams?.kids_ages,
          },
          isMobile,
        );
      } else {
        window.open(
          values.hotelOrCity?.externalUrl || values.hotelOrCity?.slug,
          isMobile ? "_self" : "_blank",
        );
      }
    }
  };

  const onSubmit: SubmitHandler<SearchAvailableRoom> = (
    values: SearchAvailableRoom,
  ) => {
    setShowValidationError(false);
    if (!values.hotelOrCity) {
      setShowValidationError(true);
      return;
    }

    if (globalThis.window !== undefined) {
      setFormData({
        ...values,
        checkIn: formatDateForStorage(range.from, DEFAULT_CHECKIN_DATE),
        checkout: formatDateForStorage(range.to, DEFAULT_CHECKOUT_DATE),
      } as any);
    }

    routeBooking(values);
  };


  return (
    <div {...props}>
      <Modal
        open={isOpen}
        toggleModal={handleToggleModal}
        containerClassName={`rounded-2xl ${isMobile ? "!max-w-[343px]" : "!max-w-[500px]"}  gap-0`}
        closeClassName="bg-[var(--fill-base-main)] text-white rounded-full opacity-100 p-1.5"
        header={
          <div className="border-b border-b-[var(--stroke-gray-light)] px-4 pt-4 pb-3 text-left text-xl leading-8 font-[var(--font-semibold)] text-[var(--text-gray-stronger)]">
            空室を検索 
          </div>
        }
        footer={<></>}
        trigger={
          trigger || (
            <Button variant="secondary" size="lg" aria-label={booking?.bookingButtonText || "空室を検索"}>
              {booking?.bookingButtonText || ""}
            </Button>
          )
        }
      >
        <div className="w-full overflow-hidden">
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="max-h-[60svh] overflow-y-auto px-4 pt-3 pb-4 md:max-h-[68vh]">
                <div className="flex flex-col gap-4">
                  <div>
                    <Label className="mb-1.5 font-[var(--font-medium)] text-[var(--text-gray-normal)]">
                      エリア名／ホテル名
                    </Label>
                    <Controller
                      name="hotelOrCity"
                      control={control}
                      render={({ field }) => (
                        <CustomSelect
                          value={field.value}
                          options={listOption}
                          onChange={(selectedOption) => {
                            if (hotelId) {
                              const currentSelection =
                                selectedOption as IHotelOrCity | null;
                              field.onChange(currentSelection);
                              setCurrentSelectedHotel(currentSelection);
                            } else {
                              field.onChange(selectedOption);
                            }
                            if (showValidationError) {
                              setShowValidationError(false);
                            }
                          }}
                          onFocus={() => {
                            if (showValidationError) {
                              setShowValidationError(false);
                            }
                          }}
                          placeholder="エリア名／ホテル名を入力してください"
                          onInputChange={(val) => setInputValue(val)}
                          inputValue={inputValue}
                          showAllButton={inputValue?.length > 0}
                          onShowAll={() => {
                            setInputValue("");
                          }}
                          error={formState.errors.hotelOrCity?.message}
                          className="w-full"
                          size="sm"
                          isLoading={loading}
                          iconCustom={<Icon icon="location-pin" />}
                          filterOption={(option, currentInputValue) => {
                            return cityAndHotelFuzzyFilter(
                              { label: option.label, data: option.data },
                              currentInputValue,
                            );
                          }}
                          styles={{
                            control: (base) => ({
                              ...base,
                              paddingLeft: "12px",
                              paddingRight: "12px",
                              gap: "8px",
                              "&:hover": {
                                borderColor:
                                  showValidationError && !field.value
                                    ? "var(--fill-red-solid-normal)"
                                    : base.borderColor,
                              },
                              borderColor:
                                showValidationError && !field.value
                                  ? "var(--fill-red-solid-normal)"
                                  : base.borderColor,
                              borderWidth:
                                showValidationError && !field.value
                                  ? "2px"
                                  : "1px",
                            }),
                            input: (base) => ({
                              ...base,
                              fontSize: "16px",
                            }),
                            placeholder: (base) => ({
                              ...base,
                              opacity: 0.6,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              fontSize: "12px",
                            }),
                            groupHeading: (base) => ({
                              ...base,
                              fontSize: "14px",
                              fontWeight: "bold",
                              color: "var(--text-gray-stronger)",
                              padding: "8px 12px",
                            }),
                          }}
                        />
                      )}
                    />
                  </div>

                  <div>
                    <Label className="mb-1.5 font-[var(--font-medium)] text-[var(--text-gray-normal)]">
                      {configurations?.checkinLabel || ""} -{" "}
                      {configurations?.checkoutLabel || ""}
                    </Label>
                    <DateRangePicker
                      range={range}
                      onChange={handleRangeSelect}
                      onCancel={() => {
                        setRange(previousRange);
                      }}
                    >
                      <Button
                        hierarchy="outline"
                        variant="neutral"
                        className="flex w-full items-center justify-between rounded-md border border-gray-300 px-3 py-4 text-sm font-medium text-[var(--text-gray-light)]"
                      >
                        <span>
                          {range?.from
                            ? format(range.from, datePickerFormatJP)
                            : configurations?.checkinLabel || ""}
                        </span>
                        <ArrowRight
                          className="text-[var(--text-gray-light)]"
                          width={16}
                          height={16}
                        />
                        <span>
                          {range?.to
                            ? format(range.to, datePickerFormatJP)
                            : configurations?.checkoutLabel || ""}
                        </span>
                        <Icon icon={"calendar"} />
                      </Button>
                    </DateRangePicker>
                  </div>

                  <div>
                    <RoomSelect
                      roomConfiguration={roomsConfigurations}
                      AgesConfigurations={agesConfigurations}
                    />
                  </div>

                  <div>
                    <Label className="font-medium text-[var(--text-gray-normal)]">
                      {configurations?.promoCodeLabel || ""}
                    </Label>
                    <Controller
                      name="promoCode"
                      control={control}
                      render={({ field }) => (
                        <Input
                          className="w-full rounded-sm border border-gray-300 placeholder:text-sm"
                          value={field.value ? field.value : promoCode || ""}
                          onChange={(e) => {
                            field.onChange(e);
                          }}
                          placeholder={
                            configurations?.promoCodePlaceholder || ""
                          }
                          size="sm"
                        />
                      )}
                    />
                  </div>
                </div>
              </div>
              
              <div className="w-full border-t border-t-[var(--stroke-gray-light)] px-4 pt-3 pb-4">
                <Button
                  variant="secondary"
                  className="flex w-full items-center justify-between"
                  size="lg"
                  type="submit"
                  disabled={!formState.isValid && formState.isSubmitted}
                >
                  <span>{booking?.bookingButtonText || ""}</span>
                  <AngleRightSmall width={20} height={20} />
                </Button>
              </div>
            </form>
          </FormProvider>
        </div>
      </Modal>
    </div>
  );
};

export { SearchAvailableRoomMobile };
